import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { Client } from '../types/users';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Building2, Settings, ChevronRight, LogOut } from 'lucide-react';
import { auth } from '../config/firebase';
import { cleanupClientSpecificData } from '../utils/localStorageCleanup';
// Multi-tab fix: cliente activo en sessionStorage (per-tab).
import {
  getStoredSelectedClientRaw,
  setStoredSelectedClientRaw,
  removeStoredSelectedClient,
} from '../utils/clientStorage';
import ClientsManagement from './admin/ClientsManagement';
import { buildClientSelection } from '../utils/clientSelection';

export default function ClientSelector() {
  const { t } = useTranslation();
  const { userData } = useAuth();
  const { setSelectedClient, setAvailableClients, isLoadingClients, setIsLoadingClients, setIsInAdminMode } = useClient();
  const [clients, setClients] = useState<Client[]>([]);
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  useEffect(() => {
    // For superadmins, ALWAYS go directly to admin panel
    if (userData?.user_type === 'superadmin') {
      const storedClient = getStoredSelectedClientRaw();
      
      // Check if already in admin mode
      if (storedClient) {
        try {
          const parsed = JSON.parse(storedClient);
          if (parsed.clientId === 'admin-mode') {
            // Already in admin mode
            setShowAdminPanel(true);
            setIsInAdminMode(true);
            setSelectedClient(parsed);
            return;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Always set admin mode for superadmins
      const adminModeClient = {
        clientId: 'admin-mode',
        clientName: 'Modo Administración',
        clientLogo: undefined
      };
      
      setShowAdminPanel(true);
      setIsInAdminMode(true);
      setSelectedClient(adminModeClient);
      setStoredSelectedClientRaw(JSON.stringify(adminModeClient));
      return;
    }

    // Check if we're already in admin mode (for admins)
    const storedClient = getStoredSelectedClientRaw();
    let previousClientId: string | null = null;
    
    if (storedClient) {
      try {
        const parsed = JSON.parse(storedClient);
        previousClientId = parsed.clientId;
        
        // If we're in admin mode, show the admin panel directly
        if (parsed.clientId === 'admin-mode') {
          setShowAdminPanel(true);
          setIsInAdminMode(true);
          setSelectedClient(parsed);
          return;
        }
      } catch (e) {
        console.error('Error parsing stored client:', e);
      }
    }
    
    // Clean up localStorage data from previous client
    cleanupClientSpecificData(previousClientId);
    
    // Load clients based on user type
    if (userData?.user_type === 'superadmin') {
      // SuperAdmins can see all clients
      loadAllClients();
    } else if (userData?.user_type === 'admin' && userData.assigned_clients) {
      loadAssignedClients(userData.assigned_clients);
    } else if (userData?.user_type === 'user' && userData.assigned_clients && userData.assigned_clients.length > 0) {
      // For regular users, auto-select their single assigned client
      loadAssignedClients(userData.assigned_clients);
    }
  }, [userData]);

  const loadAllClients = async () => {
    setIsLoadingClients(true);
    try {
      const clientsSnapshot = await getDocs(collection(db, 'clients'));
      const clientsData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Client));
      setClients(clientsData.filter(client => client.active));
      setAvailableClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const loadAssignedClients = async (clientIds: string[]) => {
    setIsLoadingClients(true);

    try {
      // Check if clientIds is empty or undefined
      if (!clientIds || clientIds.length === 0) {
        setClients([]);
        setAvailableClients([]);
        setIsLoadingClients(false);
        return;
      }

      const clientsSnapshot = await getDocs(
        query(collection(db, 'clients'), where('__name__', 'in', clientIds))
      );

      const clientsData = clientsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Client));

      const activeClients = clientsData.filter(client => client.active);

      setClients(activeClients);
      setAvailableClients(clientsData);

      // Auto-select for users with single client
      if (userData?.user_type === 'user' && activeClients.length === 1) {
        handleClientSelect(activeClients[0]);
      } else {
      }
    } catch (error) {
      console.error('[ClientSelector] Error loading assigned clients:', error);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const handleClientSelect = (client: Client) => {

    // IMPORTANT: Clear stored client first to ensure fresh data
    removeStoredSelectedClient();

    // Misma construcción que "Ir a cliente" de System Admin (PT-#87): ver utils/clientSelection.
    setSelectedClient(buildClientSelection(client.id, client));
  };

  const handleLogout = () => {
    auth.signOut();
  };

  if (isLoadingClients) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('loading')}...</p>
        </div>
      </div>
    );
  }

  // Show admin panel directly when selected
  if (showAdminPanel) {
    return <ClientsManagement />;
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('welcome')}, {userData?.name || userData?.email}
              </h1>
              <p className="text-gray-600 mt-2">
                {t('selectClient')}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t('logout')}
            </button>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {t('availableClients')}
              </h2>
              {/* Admin Panel Button for SuperAdmins */}
              {userData?.user_type === 'superadmin' && (
                <button
                  onClick={() => {
                    setShowAdminPanel(true);
                    setIsInAdminMode(true);
                    setSelectedClient({
                      clientId: 'admin-mode',
                      clientName: 'Modo Administración',
                      clientLogo: undefined
                    });
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Panel de Administración</span>
                </button>
              )}
            </div>

            {clients.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>{t('noClientsAvailable')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {clients.map((client) => (
                  <button
                    key={client.id}
                    onClick={() => handleClientSelect(client)}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-lg transition-all group"
                  >
                    <div className="flex flex-col items-center text-center space-y-4">
                      {/* Logo Section */}
                      <div className="w-full flex items-center justify-center" style={{ minHeight: '80px' }}>
                        {client.logo ? (
                          client.logo.startsWith('http') ? (
                            <img
                              src={client.logo}
                              alt={client.name}
                              style={{ maxHeight: `${client.logoHeight || 80}px`, maxWidth: '100%' }}
                              className="object-contain"
                            />
                          ) : (
                            <div
                              style={{ maxHeight: `${client.logoHeight || 80}px` }}
                              className="w-full flex items-center justify-center"
                              dangerouslySetInnerHTML={{ __html: client.logo }}
                            />
                          )
                        ) : (
                          <Building2 className="w-16 h-16 text-gray-400" />
                        )}
                      </div>

                      {/* Name and Action */}
                      <div className="w-full">
                        <h3 className="font-semibold text-lg text-gray-900 mb-2">{client.name}</h3>
                        <div className="flex items-center justify-center gap-2 text-sm text-blue-600 group-hover:text-blue-700">
                          <span>{t('clickToAccess')}</span>
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
